import { Component, Input, Output, EventEmitter, TrackByFunction, ChangeDetectionStrategy } from '@angular/core';
import {
  DataTableColumn,
  DataTableCellContext,
  DataTableHeaderContext,
  DataTableSortEvent,
  DataTableRowClickEvent,
  DataTableSelectionState,
  PageChangeEvent
} from './data-table.types';

/**
 * Data Table Component
 *
 * A reusable table component that handles common table patterns including:
 * - Column-based rendering with custom templates
 * - Row selection with master checkbox
 * - Sorting (external - emits events for server-side sorting)
 * - Pagination (client-side or server-side)
 * - Empty state display
 * - Loading overlay
 * - Clickable rows with action column support
 *
 * Usage:
 * ```html
 * <!-- Basic table -->
 * <app-data-table
 *   [data]="items"
 *   [columns]="columns"
 *   trackByKey="id">
 * </app-data-table>
 *
 * <!-- With selection and sorting -->
 * <app-data-table
 *   [data]="bookings"
 *   [columns]="tableColumns"
 *   [selectable]="true"
 *   [(selected)]="selectedItems"
 *   [sortColumn]="sortCol"
 *   [sortDirection]="sortDir"
 *   (sortChange)="onSort($event)"
 *   (rowClick)="onRowClick($event)">
 * </app-data-table>
 *
 * <!-- With client-side pagination -->
 * <app-data-table
 *   [data]="allItems"
 *   [columns]="columns"
 *   [paginated]="true"
 *   [pageSize]="10"
 *   [pageSizeOptions]="[10, 25, 50]"
 *   (pageChange)="onPageChange($event)">
 * </app-data-table>
 *
 * <!-- With server-side pagination -->
 * <app-data-table
 *   [data]="currentPageItems"
 *   [columns]="columns"
 *   [paginated]="true"
 *   [page]="currentPage"
 *   [pageSize]="pageSize"
 *   [total]="totalItemCount"
 *   (pageChange)="loadPage($event)">
 * </app-data-table>
 *
 * <!-- With custom cell templates -->
 * <ng-template #statusCell let-row let-value="value">
 *   <span class="badge" [ngClass]="getStatusClass(row.status)">{{ value }}</span>
 * </ng-template>
 * ```
 */
@Component({
  selector: 'app-data-table',
  standalone: false,
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent<T = unknown> {
  // ========================================
  // DATA INPUTS
  // ========================================

  /** Array of data items to display in the table */
  @Input() data: T[] = [];

  /** Column definitions including key, label, and optional templates */
  @Input() columns: DataTableColumn<T>[] = [];

  /** Property key used for trackBy function (defaults to 'id') */
  @Input() trackByKey: keyof T = 'id' as keyof T;

  // ========================================
  // SELECTION INPUTS
  // ========================================

  /** Whether to show row selection checkboxes */
  @Input() selectable = false;

  /** Array of currently selected items (for two-way binding) */
  @Input() selected: T[] = [];

  // ========================================
  // SORTING INPUTS (External)
  // ========================================

  /** Currently sorted column key */
  @Input() sortColumn = '';

  /** Current sort direction */
  @Input() sortDirection: 'asc' | 'desc' | '' = '';

  // ========================================
  // UI STATE INPUTS
  // ========================================

  /** Whether the table is in loading state */
  @Input() loading = false;

  /** Whether rows are clickable (adds hover effect and cursor) */
  @Input() rowClickable = false;

  /** CSS class(es) to apply to the table element */
  @Input() tableClass = 'table-v2';

  // ========================================
  // EMPTY STATE INPUTS
  // ========================================

  /** Whether to show empty state when no data */
  @Input() showEmptyState = true;

  /** Translation key for empty state title */
  @Input() emptyTitle = 'common.no_data';

  /** Translation key for empty state description (optional) */
  @Input() emptyDescription = '';

  /** Icon class for empty state */
  @Input() emptyIcon = 'inbox';

  // ========================================
  // PAGINATION INPUTS
  // ========================================

  /** Whether to enable client-side pagination */
  @Input() paginated = false;

  /** Current page number (1-indexed) */
  @Input() page = 1;

  /** Number of items per page */
  @Input() pageSize = 10;

  /** Total number of items (for server-side pagination, defaults to data.length) */
  @Input() total?: number;

  /** Available page size options for the dropdown */
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];

  /** Whether to show page size selector in pagination */
  @Input() showPageSizeSelector = true;

  // ========================================
  // OUTPUTS
  // ========================================

  /** Emitted when a sortable column header is clicked */
  @Output() sortChange = new EventEmitter<DataTableSortEvent>();

  /** Emitted when selection state changes (includes allSelected, indeterminate flags) */
  @Output() selectionChange = new EventEmitter<DataTableSelectionState<T>>();

  /** Emitted when selected items array changes (for two-way binding: selectedChange) */
  @Output() selectedChange = new EventEmitter<T[]>();

  /** Emitted when a row is clicked */
  @Output() rowClick = new EventEmitter<DataTableRowClickEvent<T>>();

  /** Emitted when pagination changes (page or pageSize) */
  @Output() pageChange = new EventEmitter<PageChangeEvent>();

  // ========================================
  // COMPUTED PROPERTIES
  // ========================================

  /** Returns only columns where visible is not explicitly false */
  get visibleColumns(): DataTableColumn<T>[] {
    return this.columns.filter(col => col.visible !== false);
  }

  /** Total column count including selection checkbox if enabled */
  get totalColumnCount(): number {
    return this.visibleColumns.length + (this.selectable ? 1 : 0);
  }

  /** Whether all rows are currently selected */
  get isAllSelected(): boolean {
    if (this.data.length === 0) return false;
    return this.selected.length === this.data.length;
  }

  /** Whether some (but not all) rows are selected */
  get isIndeterminate(): boolean {
    return this.selected.length > 0 && this.selected.length < this.data.length;
  }

  /** Total number of items for pagination (uses total input if provided, otherwise data.length) */
  get totalItems(): number {
    return this.total ?? this.data.length;
  }

  /**
   * Data to display in the table.
   * When pagination is enabled, returns only the items for the current page.
   * When pagination is disabled, returns all data.
   */
  get displayData(): T[] {
    if (!this.paginated) {
      return this.data;
    }

    // For client-side pagination, slice the data array
    // If total is provided (server-side pagination), assume data is already the correct page
    if (this.total !== undefined) {
      return this.data;
    }

    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    return this.data.slice(startIndex, endIndex);
  }

  // ========================================
  // TRACKBY FUNCTION
  // ========================================

  /** TrackBy function for ngFor using the configured trackByKey */
  trackByFn: TrackByFunction<T> = (_index: number, item: T): unknown => {
    return item[this.trackByKey];
  };

  // ========================================
  // CELL VALUE HELPERS
  // ========================================

  /**
   * Gets the value of a cell by column key, supporting nested properties
   * @param row The data row
   * @param key The column key (supports dot notation for nested props)
   */
  getCellValue(row: T, key: string | keyof T): unknown {
    const keyString = String(key);

    // Handle nested properties (e.g., 'hotel.name')
    if (keyString.includes('.')) {
      return keyString.split('.').reduce<unknown>((obj, k) => {
        return obj && typeof obj === 'object' ? (obj as Record<string, unknown>)[k] : undefined;
      }, row);
    }

    return row[key as keyof T];
  }

  /**
   * Creates the context object for cell templates
   */
  getCellContext(row: T, column: DataTableColumn<T>, rowIndex: number): DataTableCellContext<T> {
    return {
      $implicit: row,
      value: this.getCellValue(row, column.key),
      column,
      rowIndex
    };
  }

  /**
   * Creates the context object for header templates
   */
  getHeaderContext(column: DataTableColumn<T>): DataTableHeaderContext<T> {
    return {
      $implicit: column,
      column
    };
  }

  // ========================================
  // EVENT HANDLERS
  // ========================================

  /**
   * Handles header click for sorting
   */
  onHeaderClick(column: DataTableColumn<T>): void {
    if (!column.sortable) return;

    // Use sortKey if provided, otherwise fall back to key
    const sortKey = column.sortKey ?? String(column.key);
    let newDirection: 'asc' | 'desc';

    // Toggle direction if same column, otherwise default to ascending
    if (this.sortColumn === sortKey) {
      newDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      newDirection = 'asc';
    }

    this.sortChange.emit({
      column: sortKey,
      direction: newDirection
    });
  }

  /**
   * Handles header click via keyboard (Enter/Space)
   */
  onHeaderKeydown(event: KeyboardEvent, column: DataTableColumn<T>): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onHeaderClick(column);
    }
  }

  /**
   * Handles master checkbox change (select/deselect all)
   */
  onMasterCheckboxChange(isChecked: boolean): void {
    const newSelected = isChecked ? [...this.data] : [];

    this.emitSelectionChange(newSelected);
  }

  /**
   * Handles individual row checkbox change
   */
  onRowCheckboxChange(row: T): void {
    const isCurrentlySelected = this.isRowSelected(row);
    let newSelected: T[];

    if (isCurrentlySelected) {
      newSelected = this.selected.filter(item => item !== row);
    } else {
      newSelected = [...this.selected, row];
    }

    this.emitSelectionChange(newSelected);
  }

  /**
   * Checks if a row is currently selected
   */
  isRowSelected(row: T): boolean {
    return this.selected.includes(row);
  }

  /**
   * Emits selection change events
   */
  private emitSelectionChange(newSelected: T[]): void {
    this.selectedChange.emit(newSelected);
    this.selectionChange.emit({
      selected: newSelected,
      allSelected: newSelected.length === this.data.length && this.data.length > 0,
      indeterminate: newSelected.length > 0 && newSelected.length < this.data.length
    });
  }

  /**
   * Handles row click
   */
  onRowClick(row: T, rowIndex: number, event: MouseEvent): void {
    this.rowClick.emit({ row, rowIndex, event });
  }

  /**
   * Checks if a column is an action column (for stopPropagation handling)
   */
  isActionColumn(column: DataTableColumn<T>): boolean {
    return column.template === 'actions';
  }

  // ========================================
  // PAGINATION HANDLERS
  // ========================================

  /**
   * Handles page change from pagination component
   */
  onPageChange(event: PageChangeEvent): void {
    this.page = event.page;
    this.pageSize = event.perPage;
    this.pageChange.emit(event);
  }
}
